import { apiService } from 'core/services/apiService';
import {
  IOnboardingSettings,
  IOnboardingDocument,
  IOnboardingTemplateType,
  OnboardingDocumentType
} from 'core/interfaces/onboardingInterface';

// ========================
// General Settings (EIN + Storage Provider)
// ========================

export const getOnboardingSettings = async (workplaceId?: number): Promise<IOnboardingSettings | null> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    params.append('page', '1');
    params.append('itemsPerPage', '10');
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    
    const response = await apiService.get(`onboarding_settings?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingSettings[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items.length > 0 ? items[0] : null;
  } catch (error) {
    console.error('Error fetching onboarding settings:', error);
    return null;
  }
};

export const createOnboardingSettings = async (
  settings: Partial<IOnboardingSettings>
): Promise<IOnboardingSettings> => {
  const response = await apiService.post('onboarding-settings', settings);
  const data = apiService.responseHandler(response);
  return data;
};

// ========================
// Onboarding Template Types
// ========================

export const getOnboardingTemplateTypes = async (): Promise<IOnboardingTemplateType[]> => {
  try {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    
    const response = await apiService.get(`onboarding_template_types?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingTemplateType[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding template types:', error);
    return [];
  }
};

// ========================
// Onboarding Documents
// ========================

export const getOnboardingDocuments = async (workplaceId?: number): Promise<IOnboardingDocument[]> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    params.append('order[sortOrder]', 'asc');
    
    const response = await apiService.get(`onboarding_document_templates?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingDocument[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding documents:', error);
    return [];
  }
};

export const createOnboardingDocument = async (
  document: FormData
): Promise<IOnboardingDocument> => {
  const response = await apiService.postUpload('onboarding-document-templates', document);
  const data = apiService.responseHandler(response);
  return data;
};

export const updateOnboardingDocument = async (
  id: number,
  document: Partial<IOnboardingDocument>
): Promise<IOnboardingDocument> => {
  const response = await apiService.put(`onboarding-document-templates/${id}`, document);
  const data = apiService.responseHandler(response);
  return data;
};

export const deleteOnboardingDocument = async (id: number): Promise<void> => {
  await apiService.delete(`onboarding_document_templates/${id}`);
};

export const reorderOnboardingDocuments = async (
  documents: { id: number; sortOrder: number }[]
): Promise<void> => {
  // Update each document's sort order
  const updatePromises = documents.map(doc =>
    updateOnboardingDocument(doc.id, { sortOrder: doc.sortOrder })
  );
  await Promise.all(updatePromises);
};

// Helper function to map type IRI to enum
export const getDocumentTypeFromIRI = (typeIRI: string): OnboardingDocumentType => {
  if (typeIRI.includes('/1')) return OnboardingDocumentType.DATA_ENTRY;
  if (typeIRI.includes('/2')) return OnboardingDocumentType.UPLOAD;
  if (typeIRI.includes('/3')) return OnboardingDocumentType.POLICY;
  return OnboardingDocumentType.DATA_ENTRY; // default
};

// Helper function to get type IRI from enum
export const getDocumentTypeIRI = (documentType: OnboardingDocumentType): string => {
  const typeMap = {
    [OnboardingDocumentType.DATA_ENTRY]: '/api/onboarding_template_types/1',
    [OnboardingDocumentType.UPLOAD]: '/api/onboarding_template_types/2',
    [OnboardingDocumentType.POLICY]: '/api/onboarding_template_types/3',
  };
  return typeMap[documentType];
};

export const uploadOnboardingDocumentFile = async (
  file: File,
  name: string,
  typeId: number,
  sortOrder: number,
  workplaceId?: number
): Promise<IOnboardingDocument> => {
  const id = workplaceId || Number(localStorage.getItem("workplace-id"));
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  formData.append('type', `/api/onboarding_template_types/${typeId}`);
  formData.append('sortOrder', sortOrder.toString());
  formData.append('workplace', `/api/workplaces/${id}`);
  
  return createOnboardingDocument(formData);
};

// ========================
// Onboarding Records
// ========================

export interface CreateOnboardingPayload {
  user?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  cellPhone: string;
  type: string;
  workplace: string;
  employmentStartDate: string;
  payType: string;
  employmentType: string;
  securityRole: string;
}

export const createOnboarding = async (
  payload: CreateOnboardingPayload
): Promise<any> => {
  const response = await apiService.post('onboardings', payload);
  const data = apiService.responseHandler(response);
  return data;
};

export interface OnboardingRecord {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  user?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  cellPhone: string;
  type: string;
  status: string;
  workplace: string;
  employmentStartDate: string;
  payType: string;
  employmentType: string;
  securityRole: string;
  phase1CompletedAt?: string;
  phase2CompletedAt?: string;
  phase2ReviewedAt?: string;
  phase2ReviewStatus?: 'approved' | 'rejected';
  phase3CompletedAt?: string;
  // Basic info fields (populated after employee fills form)
  gender?: string;
  maritalStatus?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  // Phase Two Status (embedded)
  phaseTwoStatuses?: OnboardingPhaseTwoStatus;
}

export interface OnboardingListResponse {
  member?: OnboardingRecord[];
  'hydra:member'?: OnboardingRecord[];
  'hydra:totalItems'?: number;
  totalItems?: number;
}

export const getOnboardings = async (workplaceId?: number, page: number = 1, itemsPerPage: number = 30): Promise<OnboardingListResponse> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('itemsPerPage', itemsPerPage.toString());
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    params.append('order[id]', 'desc');
    
    const response = await apiService.get(`onboardings?${params.toString()}`);
    const data = apiService.responseHandler(response);
    return data;
  } catch (error) {
    console.error('Error fetching onboardings:', error);
    return { member: [] };
  }
};

export const deleteOnboarding = async (id: number): Promise<void> => {
  await apiService.delete(`onboardings/${id}`);
};

// ========================
// Onboarding Phase Two (Employee Self-Entry)
// ========================

export interface OnboardingPhaseTwoDocumentStatus {
  template_id: number;
  name: string;
  type: string;
  status: string;
  path: string;
}

export interface OnboardingPhaseTwoStatus {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  onboarding: string;
  user: string;
  progress: {
    basic_info: {
      status: string;
      editable: boolean;
    };
    documents: OnboardingPhaseTwoDocumentStatus[];
  };
  updatedAt: string;
}

export const getOnboardingPhaseTwoStatus = async (): Promise<OnboardingPhaseTwoStatus | null> => {
  try {
    const response = await apiService.get('onboarding-phase-two/status');
    const data = apiService.responseHandler(response);
    return data;
  } catch (error) {
    console.error('Error fetching onboarding phase two status:', error);
    return null;
  }
};

export const uploadOnboardingPhaseTwoDocument = async (
  file: File,
  onboardingId: number,
  templateId: number
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('onboarding', onboardingId.toString());
  formData.append('template', templateId.toString());
  
  const response = await apiService.postUpload('onboarding-phase-two/upload', formData);
  const data = apiService.responseHandler(response);
  return data;
};

export const getOnboardingById = async (id: number): Promise<OnboardingRecord | null> => {
  try {
    const response = await apiService.get(`onboardings/${id}`);
    const data = apiService.responseHandler(response);
    return data;
  } catch (error) {
    console.error('Error fetching onboarding by ID:', error);
    return null;
  }
};

export interface BasicInfoPayload {
  firstName: string;
  middleName?: string;
  lastName: string;
  cellPhone: string;
  gender: string;
  maritalStatus: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
  state: string;
  country: string;
  dateOfBirth: string;
}

export const updateOnboardingBasicInfo = async (
  onboardingId: number,
  payload: BasicInfoPayload
): Promise<any> => {
  const response = await apiService.patch(`onboardings/${onboardingId}/basic_info`, payload);
  const data = apiService.responseHandler(response);
  return data;
};

// ========================
// Employee Uploaded Documents
// ========================

export interface OnboardingEmployeeDocument {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  onboarding: string; // IRI like "/api/onboardings/123"
  template: string; // IRI like "/api/onboarding_document_templates/456"
  filePath: string;
  uploadedAt: string;
}

export const getOnboardingEmployeeDocuments = async (
  onboardingId: number
): Promise<OnboardingEmployeeDocument[]> => {
  try {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    params.append('onboarding', `/api/onboardings/${onboardingId}`);
    
    const response = await apiService.get(`onboarding_employee_documents?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: OnboardingEmployeeDocument[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    } else if (data && data['hydra:member']) {
      items = Array.isArray(data['hydra:member']) ? data['hydra:member'] : [data['hydra:member']];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding employee documents:', error);
    return [];
  }
};

// Helper function to extract ID from IRI
export const extractIdFromIri = (iri: string): number | null => {
  if (!iri) return null;
  const match = iri.match(/\/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

// ========================
// Phase 3: Manager Review Actions
// ========================

export type Phase2ReviewAction = 'approve' | 'reject' | 'reset';

export interface Phase2ReviewPayload {
  action: Phase2ReviewAction;
  reason?: string;
}

export const submitPhase2Review = async (
  onboardingId: number,
  action: Phase2ReviewAction,
  reason?: string
): Promise<any> => {
  const payload: Phase2ReviewPayload = { action };
  if (reason) {
    payload.reason = reason;
  }
  const response = await apiService.patch(`onboardings/${onboardingId}/phase2-review`, payload);
  const data = apiService.responseHandler(response);
  return data;
};

// Legacy functions for backward compatibility
export const approveOnboarding = async (onboardingId: number): Promise<any> => {
  return submitPhase2Review(onboardingId, 'approve');
};

export const rejectOnboarding = async (onboardingId: number, reason?: string): Promise<any> => {
  return submitPhase2Review(onboardingId, 'reject', reason);
};

export const resetOnboarding = async (onboardingId: number): Promise<any> => {
  return submitPhase2Review(onboardingId, 'reset');
};
